import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Calendar, ChevronRight, CircleCheckBig, Clock3, Trophy, Wifi } from 'lucide-react';
import { API_BASE_URL, PANEL_URL } from '../lib/api';
import { TurnivoLogo } from '../components/TurnivoLogo';
import '../styles/landing-cinematic.css';

type PublicTournamentItem = {
  id: string;
  name: string;
  status: 'GENERATED' | 'IN_PROGRESS' | 'FINISHED' | 'DRAFT' | string;
  scheduleStartAtIso: string | null;
  format?: string | null;
};

function formatStatus(status: PublicTournamentItem['status']): string {
  if (status === 'IN_PROGRESS') return 'Trwa';
  if (status === 'GENERATED') return 'Zaplanowany';
  if (status === 'FINISHED') return 'Zakończony';
  return 'Turniej';
}

function formatLabel(format: string | null | undefined): string {
  if (format === 'ROUND_ROBIN') return 'Każdy z każdym';
  if (format === 'GROUPS_PLAYOFF') return 'Grupy + Playoff';
  if (format === 'KNOCKOUT') return 'Pucharowy (KO)';
  if (format === 'DOUBLE_ELIMINATION') return 'Podwójna eliminacja';
  return format ?? '—';
}

function formatStartDate(value: string | null | undefined): string {
  if (!value || value.trim() === '') return 'Termin do ustalenia';
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' });
  }
  return value;
}

function sectionMeta(status: PublicTournamentItem['status']) {
  if (status === 'IN_PROGRESS') {
    return {
      title: 'Trwające',
      subtitle: 'Mecze są rozgrywane na żywo.',
      icon: <Wifi className="w-4 h-4" />,
      tag: 'LIVE',
    };
  }
  if (status === 'GENERATED') {
    return {
      title: 'Zaplanowane',
      subtitle: 'Turnieje gotowe do startu.',
      icon: <Clock3 className="w-4 h-4" />,
      tag: 'START',
    };
  }
  return {
    title: 'Zakończone',
    subtitle: 'Archiwum zakończonych wydarzeń.',
    icon: <CircleCheckBig className="w-4 h-4" />,
    tag: 'DONE',
  };
}

function TournamentCard({ t }: { t: PublicTournamentItem }) {
  const live = t.status === 'IN_PROGRESS';
  return (
    <Link to={`/turnieje/${t.id}`} className="feature-card" style={{ display: 'block', textDecoration: 'none' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: 'linear-gradient(135deg,#0066FF,#00C7B1)',
            display: 'grid',
            placeItems: 'center',
            color: '#fff',
          }}
        >
          <Trophy className="w-5 h-5" />
        </div>
        <span className="mini-tag" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {live ? <i className="status-dot" /> : null}
          {formatStatus(t.status)}
        </span>
      </div>
      <h3 style={{ marginTop: 18, marginBottom: 10 }}>{t.name}</h3>
      <p style={{ margin: 0, fontSize: 13, color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Calendar className="w-4 h-4" />
        {formatStartDate(t.scheduleStartAtIso)}
      </p>
      <p style={{ marginTop: 8, marginBottom: 0, fontSize: 13, color: 'var(--muted)' }}>
        Format: {formatLabel(t.format)}
      </p>
      <div
        className="text-link"
        style={{ marginTop: 18, fontSize: 13, borderBottom: 'none', paddingBottom: 0, gap: 8 }}
      >
        Zobacz turniej <ChevronRight className="w-4 h-4" color="#00C7B1" />
      </div>
    </Link>
  );
}

function TournamentSection({
  status,
  items,
}: {
  status: 'IN_PROGRESS' | 'GENERATED' | 'FINISHED';
  items: PublicTournamentItem[];
}) {
  const meta = sectionMeta(status);
  return (
    <section style={{ marginTop: 48 }}>
      <div className="section-heading" style={{ marginBottom: 22 }}>
        <div>
          <div className="eyebrow" style={{ color: 'var(--lime)', marginBottom: 10 }}>
            {meta.icon}
            <span style={{ marginLeft: 8 }}>{meta.tag}</span>
          </div>
          <h2 style={{ fontSize: 32 }}>{meta.title}</h2>
          <p style={{ marginTop: 8, color: 'var(--muted)', fontSize: 14 }}>{meta.subtitle}</p>
        </div>
        <span className="mini-tag">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <div className="feature-card" style={{ color: 'var(--muted)', fontSize: 14 }}>
          Brak turniejów w tej sekcji.
        </div>
      ) : (
        <div className="feature-grid">
          {items.map((t) => (
            <TournamentCard key={t.id} t={t} />
          ))}
        </div>
      )}
    </section>
  );
}

export default function PublicTournamentsPage() {
  const [items, setItems] = useState<PublicTournamentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let canceled = false;
    setLoading(true);
    setError(null);

    fetch(`${API_BASE_URL}/api/public/tournaments`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data?.items) ? data.items : [];
        if (!canceled) setItems(list);
      })
      .catch(() => {
        if (!canceled) setError('Nie udało się pobrać listy turniejów publicznych.');
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });

    return () => {
      canceled = true;
    };
  }, []);

  const inProgress = useMemo(() => items.filter((t) => t.status === 'IN_PROGRESS'), [items]);
  const planned = useMemo(() => items.filter((t) => t.status === 'GENERATED'), [items]);
  const finished = useMemo(() => items.filter((t) => t.status === 'FINISHED'), [items]);

  return (
    <div className="landing-cinematic motion-ready">
      <header className="header">
        <div className="nav wrap">
          <TurnivoLogo className="brand brand-portal" />
          <nav className="desktop-nav" aria-label="Nawigacja">
            <Link to="/">Strona główna</Link>
            <Link to="/turnieje">Turnieje</Link>
            <a href="/#cennik">Cennik</a>
          </nav>
          <div className="nav-actions">
            <a href={`${PANEL_URL}/login`} className="login">
              Zaloguj się
            </a>
            <Link to="/register" className="button small">
              Zacznij teraz <span aria-hidden="true">↗</span>
            </Link>
          </div>
        </div>
      </header>

      <main id="main" className="section wrap" style={{ paddingTop: 56, paddingBottom: 80 }}>
        <div className="section-heading">
          <div>
            <div className="eyebrow">TURNIEJE PUBLICZNE</div>
            <h1 style={{ fontSize: 'clamp(40px, 6vw, 64px)', letterSpacing: '-2px', marginTop: 14, lineHeight: 1.05 }}>
              Aktywne rozgrywki.
              <br />
              <span className="lime">Jeden podgląd.</span>
            </h1>
          </div>
          <p>
            Trwające, zaplanowane i zakończone turnieje — wyniki live, drabinki i tabele dostępne bez
            logowania.
          </p>
        </div>

        {loading ? (
          <div className="feature-card" style={{ color: 'var(--muted)' }}>
            Ładowanie turniejów…
          </div>
        ) : null}
        {error ? (
          <div
            className="feature-card"
            style={{ borderColor: '#7F1D1D', background: '#450A0A', color: '#FECACA' }}
          >
            {error}
          </div>
        ) : null}

        {!loading && !error ? (
          items.length === 0 ? (
            <div className="feature-card" style={{ color: 'var(--muted)' }}>
              Brak publicznych turniejów do wyświetlenia.
            </div>
          ) : (
            <>
              <TournamentSection status="IN_PROGRESS" items={inProgress} />
              <TournamentSection status="GENERATED" items={planned} />
              <TournamentSection status="FINISHED" items={finished} />
            </>
          )
        ) : null}
      </main>

      <footer className="footer wrap">
        <div className="footer-top">
          <TurnivoLogo className="brand brand-portal" textSize="text-lg" boxSize="h-10 w-10" iconSize="w-5 h-5" />
          <p>Publiczny podgląd turniejów siatkarskich.</p>
          <Link to="/">
            Wróć na stronę główną <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </footer>
    </div>
  );
}
